import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';
import moment from 'moment-timezone';
import * as appApi from '../../shared/globalcontants';
import { CommonService } from '../../shared/services/common.service';
import { applyEatecTokenPayloadToStorage } from './eatec-token-session.helper';

const TICK_MS = 45000;
const REFRESH_WITHIN_SEC = 300;

/**
 * Proactive Eatec JWT refresh (Identity eatec/refreshtoken). Uses HttpBackend to bypass interceptors (no loader, no Bearer).
 * Does not interact with ng-idle or idle localStorage keys.
 */
@Injectable({ providedIn: 'root' })
export class EatecTokenRefreshService implements OnDestroy {
  private static activeInstance: EatecTokenRefreshService | null = null;

  private http: HttpClient;
  private tickSub: Subscription | null = null;
  private refreshInProgress = false;

  constructor(httpBackend: HttpBackend, private readonly commonService: CommonService) {
    this.http = new HttpClient(httpBackend);
    EatecTokenRefreshService.activeInstance = this;
  }

  static stopRefreshTimer(): void {
    EatecTokenRefreshService.activeInstance?.stop();
  }

  ngOnDestroy(): void {
    this.stop();
    if (EatecTokenRefreshService.activeInstance === this) {
      EatecTokenRefreshService.activeInstance = null;
    }
  }

  start(): void {
    this.stop();
    this.tickSub = timer(0, TICK_MS).subscribe(() => this.onTick());
  }

  stop(): void {
    if (this.tickSub) {
      this.tickSub.unsubscribe();
      this.tickSub = null;
    }
    this.refreshInProgress = false;
  }

  /**
   * Shared JSON refresh used by timer and 401 retry path.
   */
  performEatecRefresh(): Observable<any> {
    const raw = localStorage.getItem(appApi.userSessionName);
    if (!raw) {
      return throwError(() => new Error('No session'));
    }
    let user: any;
    try {
      user = JSON.parse(raw);
    } catch {
      return throwError(() => new Error('Bad session'));
    }
    const refreshToken = user.refresh_token;
    const sessionIdStr = localStorage.getItem(appApi.EatecSessionId);
    const tenantName = localStorage.getItem('tenant');
    const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');
    const cid =
      this.commonService.SystemConfig?.Cid ?? systemConfig?.Cid ?? null;
    const csInfo =
      this.commonService.SystemConfig?.CSInfo ?? systemConfig?.CSInfo ?? null;

    if (!refreshToken || !sessionIdStr || !tenantName || !cid || !csInfo) {
      return throwError(() => new Error('Missing refresh inputs'));
    }

    let sessionGuid: string = sessionIdStr;
    if (sessionGuid.includes('{')) {
      try {
        const o = JSON.parse(sessionGuid);
        sessionGuid = o?.sessionid ?? o?.SessionId ?? sessionGuid;
      } catch {
        /* use as-is */
      }
    }

    const base = window.localStorage.getItem('apiUrl') || '';
    const url = `${base}${appApi.apiURLs.UserManagementApi.EatecRefreshToken}`;
    const body = {
      refreshToken,
      sessionId: sessionGuid,
      tenantName,
      cid,
      csInfo
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      tenant: tenantName
    });

    return this.http.post<any>(url, body, { headers }).pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, index) => {
            if (index >= 2) {
              return throwError(() => error);
            }
            return timer((index + 1) * 800);
          })
        )
      )
    );
  }

  private onTick(): void {
    if (!localStorage.getItem(appApi.userSessionName)) {
      return;
    }
    if (!this.commonService.isonlineStatus) {
      return;
    }
    const refreshToken = (() => {
      try {
        return JSON.parse(localStorage.getItem(appApi.userSessionName) || '{}')?.refresh_token;
      } catch {
        return null;
      }
    })();
    if (!refreshToken) {
      return;
    }
    const expStr = localStorage.getItem(appApi.AccessTokenExpiresAtUnix);
    const exp = parseInt(expStr || '', 10);
    if (!exp) {
      return;
    }
    const now = moment().unix();
    if (now < exp - REFRESH_WITHIN_SEC) {
      return;
    }
    if (this.refreshInProgress) {
      return;
    }
    this.refreshInProgress = true;
    this.performEatecRefresh().subscribe({
      next: (res) => {
        this.refreshInProgress = false;
        if (res?.access_token || res?.AccessToken) {
          applyEatecTokenPayloadToStorage(res);
        }
      },
      error: () => {
        this.refreshInProgress = false;
      }
    });
  }
}
